<script setup lang="ts">
import { EvaluationStateForResult, MeasurementStatus } from '@/api/generator';
import { I18nKey } from '@/utils/typings';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const {
  status = undefined,
  outcome = undefined,
  size = 'default',
} = defineProps<{
  status?: MeasurementStatus;
  outcome?: EvaluationStateForResult;
  size?: 'default' | 'compact';
}>();

const active = computed(() => status && status !== 'Finished' && status !== 'Error');

const sizeClasses = computed(() => ({
  text: size === 'compact' ? 'text-body-1' : 'text-h3',
  container: size === 'compact' ? '' : 'w-fill',
  fontWeight: size === 'compact' ? 'font-weight-bold' : '',
  padding: size === 'compact' ? 'pb-1 pt-1 px-3' : 'pb-2 pt-2 px-4',
}));

// Animated ellipsis, left to right
const ellipsisDots = ref<number>(0);
let ellipsisHandle: number | null = null;

function stopEllipsis() {
  if (ellipsisHandle) {
    clearInterval(ellipsisHandle);
    ellipsisHandle = null;
  }
  ellipsisDots.value = 0;
}

function startEllipsis() {
  stopEllipsis();
  ellipsisHandle = window.setInterval(() => {
    ellipsisDots.value = (ellipsisDots.value + 1) % 4; // 0.3s intervals
  }, 333);
}

// Start/stop ellipsis based on status
watch(
  () => status,
  (statusValue) => {
    if (statusValue === 'Initializing') {
      startEllipsis();
    } else {
      stopEllipsis();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  stopEllipsis();
});
</script>

<template>
  <div
    v-if="status || outcome"
    :class="sizeClasses.container"
    style="padding-top: 0.25rem; padding-bottom: 0.25rem"
  >
    <div
      v-if="status === 'Initializing' || status === 'ReadyForBeam'"
      class="d-flex justify-center bg-primary-lighten-5 rounded"
      :class="[sizeClasses.container, sizeClasses.padding]"
    >
      <span :class="[sizeClasses.text, sizeClasses.fontWeight, 'ellipsis-container']">
        <span class="status-text">{{ $t(`MeasurementStatus.${status}` as I18nKey) }}</span>
        <span v-if="status === 'Initializing'" class="ellipsis-dots" aria-hidden="true">
          <span class="dot" :class="{ on: ellipsisDots >= 1 }">.</span>
          <span class="dot" :class="{ on: ellipsisDots >= 2 }">.</span>
          <span class="dot" :class="{ on: ellipsisDots >= 3 }">.</span>
        </span>
      </span>
    </div>
    <span
      v-else
      class="d-flex justify-center"
      :class="[
        sizeClasses.text,
        sizeClasses.fontWeight,
        sizeClasses.padding,
        { barberpole: active, 'bg-primary-lighten-5': !active },
      ]"
    >
      {{ $t(`MeasurementStatus.${status}` as I18nKey) }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.ellipsis-container {
  position: relative;
  display: inline-block;
}
.status-text {
  position: relative;
  z-index: 1;
}
.ellipsis-dots {
  position: relative;
  display: inline-flex;
  gap: 4px;
  margin-left: 6px;
}
.dot {
  opacity: 0;
  transition: opacity 80ms linear;
}
.dot.on {
  opacity: 1;
}
</style>
